/**
 * The audit log API module (wave AUD frontend items 1, 2 and 3).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Three read-only routes:
 *   GET /api/audit/{object_type}/{object_id}   one record's history (and its parts)
 *   GET /api/audit                             the auditor's register across all 14 chains
 *   GET /api/audit/heads                       every family's current head for offline verification
 *
 * A principal without the grant receives 404 (absence) from the server; the UI offers
 *neither the history drawer control nor the navigation entry when the probe answers 404.
 */

import { ApiError, apiJson, bool, isRecord, num, optNum, optStr, query, str } from "./client";

export const AUDIT_FAMILIES = [
  "CONFIGURATION",
  "PROJECT",
  "CONTRACT",
  "PARTY",
  "PERSON_ROLE",
  "ITP",
  "VERIFICATION",
  "FINDING",
  "ACTION_REQUEST",
  "CREDENTIAL",
  "STANDARD",
  "COMMUNICATION",
  "DOCUMENT",
  "SYSTEM",
] as const;

export const AUDIT_EVENT_TYPES = [
  "CONFIGURATION_CREATE",
  "CONFIGURATION_UPDATE",
  "CONFIGURATION_PUBLISH",
  "CONFIGURATION_RETIRE",
  "CONFIGURATION_ENFORCEMENT_OVERRIDE",
  "WORKFLOW_TRANSITION",
  "WORLD_PURGE",
  "AUDITED_READ",
  "GENESIS",
  "RECORD_CREATE",
  "RECORD_EDIT",
  "RECORD_REMOVE",
] as const;

export type AuditRecordType =
  | "PROJECT"
  | "CONTRACT"
  | "PARTY"
  | "ITP"
  | "ITP_ITEM"
  | "SCHEDULE"
  | "SCHEDULED_ACTIVITY"
  | "VERIFICATION"
  | "FINDING"
  | "PUNCH_LIST"
  | "ACTION_REQUEST"
  | "CREDENTIAL"
  | "DECISION_RULE"
  | "CHECKLIST_BANK";

export interface AuditChange {
  readonly field: string;
  readonly before: unknown;
  readonly after: unknown;
}

export interface AuditEntry {
  readonly id: string;
  readonly family: string;
  readonly seq: number;
  readonly eventType: string;
  readonly objectType: string;
  readonly objectId: string | null;
  readonly parentType: string | null;
  readonly parentId: string | null;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly changes: readonly AuditChange[];
  readonly actorPersonId: string | null;
  readonly actorDisplayName: string | null;
  readonly actorPartyId: string | null;
  readonly actorPartyName: string | null;
  readonly actorMechanism: string | null;
  readonly requestId: string | null;
  readonly createdAt: string;
}

export interface ChainVerdict {
  readonly family: string;
  readonly intact: boolean;
  readonly entriesChecked: number;
  readonly brokenAtSeq: number | null;
  readonly reason: string | null;
}

export interface AuditRegister {
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  readonly items: readonly AuditEntry[];
  readonly chains: readonly ChainVerdict[];
}

export interface AuditHead {
  readonly family: string;
  readonly headSeq: number;
  readonly headHash: string;
  readonly genesisAt: string | null;
  readonly count: number;
}

export interface AuditHeads {
  readonly tenantId: string;
  readonly generatedAt: string;
  readonly families: readonly AuditHead[];
  /** The verbatim JSON document returned by GET /api/audit/heads, for the offline verifier. */
  readonly rawJson: string;
}

export interface RecordHistory {
  readonly objectType: string;
  readonly objectId: string;
  readonly items: readonly AuditEntry[];
}

export interface AuditRegisterFilters {
  readonly family?: string | undefined;
  readonly objectType?: string | undefined;
  readonly objectId?: string | undefined;
  readonly actorPersonId?: string | undefined;
  readonly eventType?: string | undefined;
  readonly since?: string | undefined;
  readonly until?: string | undefined;
  readonly limit?: number | undefined;
  readonly offset?: number | undefined;
}

function decodeChange(raw: unknown): AuditChange | null {
  if (!isRecord(raw) || typeof raw.field !== "string") {
    return null;
  }
  return {
    field: raw.field,
    before: "before" in raw ? raw.before : null,
    after: "after" in raw ? raw.after : null,
  };
}

export function decodeAuditEntry(raw: Record<string, unknown>): AuditEntry {
  const rawChanges = Array.isArray(raw.changes) ? raw.changes : [];
  const changes: AuditChange[] = [];
  for (const item of rawChanges) {
    const parsed = decodeChange(item);
    if (parsed !== null) {
      changes.push(parsed);
    }
  }
  return {
    id: str(raw.id),
    family: typeof raw.family === "string" ? raw.family : "CONFIGURATION",
    seq: num(raw.seq),
    eventType: str(raw.event_type),
    objectType: str(raw.object_type),
    objectId: optStr(raw.object_id),
    parentType: optStr(raw.parent_type),
    parentId: optStr(raw.parent_id),
    payload: isRecord(raw.payload) ? raw.payload : {},
    changes,
    actorPersonId: optStr(raw.actor_person_id),
    actorDisplayName: optStr(raw.actor_display_name),
    actorPartyId: optStr(raw.actor_party_id),
    actorPartyName: optStr(raw.actor_party_name),
    actorMechanism: optStr(raw.actor_mechanism),
    requestId: optStr(raw.request_id),
    createdAt: str(raw.created_at),
  };
}

function isAuditEntryRecord(raw: unknown): raw is Record<string, unknown> {
  return (
    isRecord(raw) &&
    typeof raw.id === "string" &&
    typeof raw.seq === "number" &&
    typeof raw.event_type === "string" &&
    typeof raw.object_type === "string" &&
    typeof raw.created_at === "string"
  );
}

/**
 * GET /api/audit/{object_type}/{object_id}.
 * Answers 404 when the caller lacks AUDIT_LOG READ or cannot see the record.
 */
export async function getRecordHistory(
  token: string,
  objectType: AuditRecordType,
  objectId: string,
  signal?: AbortSignal,
): Promise<RecordHistory> {
  return apiJson(
    token,
    `/audit/${encodeURIComponent(objectType)}/${encodeURIComponent(objectId)}`,
    (raw): RecordHistory => {
      if (!isRecord(raw) || !Array.isArray(raw.items)) {
        return { objectType, objectId, items: [] };
      }
      return {
        objectType: typeof raw.object_type === "string" ? raw.object_type : objectType,
        objectId: typeof raw.object_id === "string" ? raw.object_id : objectId,
        items: raw.items.filter(isAuditEntryRecord).map(decodeAuditEntry),
      };
    },
    { signal },
  );
}

function decodeChainVerdict(raw: Record<string, unknown>): ChainVerdict {
  return {
    family: str(raw.family),
    intact: bool(raw.intact),
    entriesChecked: num(raw.entries_checked),
    brokenAtSeq: optNum(raw.broken_at_seq),
    reason: optStr(raw.reason),
  };
}

/**
 * GET /api/audit: filtered and paged across all 14 chains, with per-family chain verdicts.
 */
export async function getAuditRegister(
  token: string,
  filters?: AuditRegisterFilters,
  signal?: AbortSignal,
): Promise<AuditRegister> {
  const qs = query({
    family: filters?.family !== undefined && filters.family !== "" ? filters.family : undefined,
    object_type:
      filters?.objectType !== undefined && filters.objectType !== ""
        ? filters.objectType
        : undefined,
    object_id:
      filters?.objectId !== undefined && filters.objectId !== "" ? filters.objectId : undefined,
    actor_person_id:
      filters?.actorPersonId !== undefined && filters.actorPersonId !== ""
        ? filters.actorPersonId
        : undefined,
    event_type:
      filters?.eventType !== undefined && filters.eventType !== "" ? filters.eventType : undefined,
    since: filters?.since !== undefined && filters.since !== "" ? filters.since : undefined,
    until: filters?.until !== undefined && filters.until !== "" ? filters.until : undefined,
    limit: filters?.limit,
    offset: filters?.offset,
  });
  return apiJson(
    token,
    `/audit${qs}`,
    (raw): AuditRegister => {
      if (!isRecord(raw)) {
        throw new Error("Expected the audit register body");
      }
      const items = Array.isArray(raw.items) ? raw.items.filter(isAuditEntryRecord) : [];
      const chains = Array.isArray(raw.chains) ? raw.chains.filter(isRecord) : [];
      return {
        total: typeof raw.total === "number" ? raw.total : items.length,
        limit: typeof raw.limit === "number" ? raw.limit : 50,
        offset: typeof raw.offset === "number" ? raw.offset : 0,
        items: items.map(decodeAuditEntry),
        chains: chains.map(decodeChainVerdict),
      };
    },
    { signal },
  );
}

/**
 * GET /api/audit/heads: each family's head seq, head hash, genesis instant and count,
 * plus the verbatim JSON document as returned by the API for the offline verifier.
 */
export async function getAuditHeads(token: string, signal?: AbortSignal): Promise<AuditHeads> {
  let response: Response;
  try {
    response = await fetch("/api/audit/heads", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      signal: signal ?? null,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new ApiError("network", 0, null);
  }
  if (!response.ok) {
    const kind =
      response.status === 404
        ? "absence"
        : response.status === 403
          ? "forbidden"
          : response.status === 409
            ? "conflict"
            : response.status === 422
              ? "invalid"
              : "unavailable";
    throw new ApiError(kind, response.status, null);
  }
  const rawJson = await response.text();
  const raw: unknown = JSON.parse(rawJson);
  if (!isRecord(raw)) {
    throw new Error("Expected the audit heads body");
  }
  const families = Array.isArray(raw.families) ? raw.families.filter(isRecord) : [];
  return {
    tenantId: typeof raw.tenant_id === "string" ? raw.tenant_id : "",
    generatedAt: typeof raw.generated_at === "string" ? raw.generated_at : "",
    families: families.map((one) => ({
      family: str(one.family),
      headSeq: num(one.head_seq),
      headHash: str(one.head_hash),
      genesisAt: optStr(one.genesis_at),
      count: num(one.count),
    })),
    rawJson,
  };
}

/**
 * Probe whether this principal holds AUDIT_LOG READ.
 * A principal without the grant receives 404 (absence) from GET /api/audit/heads.
 */
export async function probeAuditAccess(token: string): Promise<boolean> {
  try {
    await apiJson(token, "/audit/heads", () => undefined);
    return true;
  } catch (error) {
    if (error instanceof ApiError && (error.kind === "absence" || error.kind === "forbidden")) {
      return false;
    }
    throw error;
  }
}
